import * as api from '../api';

import { AuthStore } from './utils/store';
import { SRPHelper } from './utils/srp';

export async function loadSettings() {
    return await api.get('/api/settings');
}

const DISABLED_ACCOUNT_CODE = 611;
const PB5_NO_SRP_CONFIGURATION_CODE = 621;
const PB6_NO_SRP_CONFIGURATION_CODE = 622;

async function checkInstallation(account) {
    const store = AuthStore.load(account);
    const identifier = store.getIdentifier();
    try {
        const id = await installDevice(identifier);
        const installation = store.getInstallation();
        const device = store.getDevice();
        if (installation !== id || !device) {
            store.saveInstalation(id);
            AuthStore.save(store);
        }
    } catch (e) {
        AuthStore.remove(store);
        throw e;
    }
}

export class AuthService {
    static async findAccount(code) {
        const response = await api.post('/api/account/verification', { code });
        return response['account'];
    }

    static async sendCodeToBackupPhone() {
        const response = await api.get('/api/account/2step?type=backup_phone');
        return response;
    }

    static async MFAVerify(code, sended_to_backup_phone) {
        const response = await api.post('/api/account/2step', {
            code,
            sended_to_backup_phone,
        });
        return response;
    }

    static async MFAVerifyRecoveryCode(code) {
        const response = await api.post('/api/account/2step', {
            code,
            type: 'recovery_code',
        });
        return response;
    }

    static async ConfirmPassword(username, password) {
        const store = AuthStore.load(username);
        const data = await initSRP(username);
        const srp = new SRPHelper(data.N, data.g, data.k, data.salt);
        const device = store.getDevice();
        const challenge = await challengeSRP(
            username,
            device,
            srp.largeAValue.toString(16)
        );
        const proof = srp.calculateProof(
            username,
            password,
            challenge.server_public_key
        );
        let response = await proofSRP(
            username,
            device,
            proof.toString(16),
            true
        );

        return response.confirm_code;
    }

    static async checkVerification(account, srpEnforced = false) {
        await checkInstallation(account);

        const store = AuthStore.load(account);
        const device = store.getDevice();
        if (device) {
            const response = await checkAccount(account, device);
            if (response.error) {
                const checkError = getVerificationError(response.id);
                if (checkError) return checkError;
            }
            if (response.status.enabled == false) {
                return { disabled: true };
            }
            if (response.status.device) {
                return { verification: false };
            }
            store.removeDevice();
            AuthStore.save(store);
        }
        const createResp = await createVerification(account, srpEnforced);
        if (createResp.error) {
            const createError = getVerificationError(createResp.id);
            if (createError) return createError;
        }
        return { verification: true };
    }

    static async verification(account, verification) {
        const store = AuthStore.load(account);
        const installation = store.getInstallation();
        const { error, device } = await sendVerification(
            account,
            verification,
            installation
        );
        if (error) {
            return { incorrect: true };
        }
        if (device) {
            store.saveDevice(device);
            AuthStore.save(store);
        }
        return {};
    }

    static async login(account, password) {
        const store = AuthStore.load(account);
        const device = store.getDevice();
        try {
            const data = await initSRP(account);
            const srp = new SRPHelper(data.N, data.g, data.k, data.salt);
            const challenge = await challengeSRP(
                account,
                device,
                srp.largeAValue.toString(16)
            );
            const proof = srp.calculateProof(
                account,
                password,
                challenge.server_public_key
            );
            const response = await proofSRP(
                account,
                device,
                proof.toString(16)
            );
            return response;
        } catch (e) {
            console.warn(e);
            return { status: 'error', message: e.message };
        }
    }

    static async password(account, password, code) {
        const data = await initSRP(account);
        const srp = new SRPHelper(data.N, data.g, data.k, data.salt);
        const verifier = srp.generateVerifier(account, password).toString(16);
        await api.post('/api/account/srp', {
            account,
            verifier,
            salt: srp.salt,
            code,
        });
    }

    static async resetPassword(email) {
        const { status } = await api.post('/api/account/reset', {
            email,
        });
        return status;
    }

    static fetchWebAppSSOCode() {
        return api.post('/api/webapp_sso_code');
    }
}

async function installDevice(identifier) {
    const { id } = await api.post('/api/device/install', { identifier });
    if (id) {
        return id;
    }
    throw new Error('Install device failed');
}

export async function checkAccount(account, device) {
    const check = await api.post('/api/account/check', {
        account,
        device,
    });
    return check;
}

export async function createVerification(account, srpEnforced) {
    return await api.post('/api/device/register', { account, srpEnforced });
}

async function sendVerification(account, verification, installation) {
    const { error, device } = await api.post('/api/device/confirm', {
        account,
        verification,
        installation,
    });
    if (error || device) {
        return { error, device };
    }
    throw new Error('Verification faild');
}

async function sendToSRP(data) {
    const response = await api.post('/api/account/srp', data);
    if (response.error) {
        throw new TypeError(response.error);
    }
    if (response.status && response.status === 'error') {
        throw new TypeError(response.message);
    }
    return response;
}

export async function initSRP(account) {
    const response = await sendToSRP({ account });
    if (response.srp) {
        return response.srp;
    }
    throw new TypeError();
}

export async function challengeSRP(account, device, client_public_key) {
    return await sendToSRP({
        account,
        device,
        client_public_key,
    });
}

export async function proofSRP(account, device, proof, passConfirm = false) {
    return await sendToSRP({
        account,
        device,
        proof,
        pass_confirm: passConfirm,
    });
}

function getVerificationError(error) {
    switch (error) {
        case PB5_NO_SRP_CONFIGURATION_CODE:
            return { PB5NoSRP: true };
        case PB6_NO_SRP_CONFIGURATION_CODE:
            return { PB6NoSRP: true };
        case DISABLED_ACCOUNT_CODE:
            return { disabled: true };
        default:
            return null;
    }
}

export const loadSubscriptionInvitation = async (invitation, subscription) =>
    api.checkResponse(
        api.get(`/api/subscription/migrate`, {
            invitation: invitation,
            subscription: subscription,
        })
    );

export const loadOrganizationInvitation = (invitation, organization) =>
    api.checkResponse(
        api.get(`/api/organization/migrate`, {
            invitation: invitation,
            organization: organization,
        })
    );

export const migrateAccountOrganization = (invitation, organization) => {
    const data = {
        invitation: invitation,
        organization: organization,
    };
    return api.checkResponse(api.post(`/api/organization/migrate`, data));
};

export const migrateAccountSubscription = (invitation, subscription) => {
    const data = {
        invitation: invitation,
        subscription: subscription,
    };
    return api.checkResponse(api.post(`/api/subscription/migrate`, data));
};
